import { useLiveQuery } from 'dexie-react-hooks'
import { db } from './dexie'
import type {
  CatDiametro,
  CatMaterialRede,
  CatObra,
  CatProjeto,
  CatServico,
  CatTipoRede,
  OutCarreteiro,
  OutParteDiaria,
  OutFoto,
  OutProducao,
  SyncStatus,
} from './entities'

/**
 * DAOs em forma de hooks React (via `useLiveQuery`). Cada hook observa o Dexie e re-renderiza
 * automaticamente quando os dados mudam (substitui Flow do Room).
 *
 * Source of truth = Dexie. Refresh de rede é orquestrado por `core/sync/catalogSync.ts`
 * e não afeta esses hooks diretamente — assim que o Dexie atualiza, a UI segue.
 */

// ============================================================================
//  Projetos
// ============================================================================
export function useProjetos(): CatProjeto[] | undefined {
  return useLiveQuery(() => db.cat_projeto.orderBy('codigo').toArray())
}

export function useProjeto(id: number | null | undefined): CatProjeto | undefined {
  return useLiveQuery(
    async (): Promise<CatProjeto | undefined> => {
      if (id == null) return undefined
      return db.cat_projeto.get(id)
    },
    [id],
  )
}

/** Projetos com contagem de serviços vinculados — útil pra mostrar no picker. */
export function useProjetosComContagem():
  | Array<{ id: number; codigo: string; qtdServicos: number }>
  | undefined {
  return useLiveQuery(async () => {
    const projetos = await db.cat_projeto.orderBy('codigo').toArray()
    const all = await db.cat_projeto_servico.toArray()
    const counts = new Map<number, number>()
    for (const v of all) {
      counts.set(v.projetoId, (counts.get(v.projetoId) ?? 0) + 1)
    }
    return projetos
      .map((p) => ({ ...p, qtdServicos: counts.get(p.id) ?? 0 }))
      .sort((a, b) => b.qtdServicos - a.qtdServicos || a.codigo.localeCompare(b.codigo))
  })
}

// ============================================================================
//  Obras
// ============================================================================
export function useObras(): CatObra[] | undefined {
  return useLiveQuery(() => db.cat_obra.orderBy('nome').toArray())
}

export function useObrasPorProjeto(projetoId: number | null | undefined): CatObra[] | undefined {
  return useLiveQuery(
    async (): Promise<CatObra[]> => {
      if (projetoId == null) return []
      return db.cat_obra.where('projetoId').equals(projetoId).sortBy('nome')
    },
    [projetoId],
  )
}

// ============================================================================
//  Serviços filtrados por projeto (via cat_projeto_servico)
// ============================================================================
export function useServicosDoProjeto(
  projetoId: number | null | undefined,
  opts: { apenasCarreteiro?: boolean } = {},
): CatServico[] | undefined {
  const { apenasCarreteiro = false } = opts
  return useLiveQuery(async () => {
    if (projetoId == null) return []
    const vinculos = await db.cat_projeto_servico.where('projetoId').equals(projetoId).toArray()
    if (vinculos.length === 0) return []
    const ids = vinculos.map((v) => v.servicoId)
    const servicos = await db.cat_servico.bulkGet(ids)
    return servicos
      .filter((s): s is CatServico => s != null)
      .filter((s) => (apenasCarreteiro ? s.tp === 'C' : s.tp !== 'C'))
      .sort((a, b) => a.nome.localeCompare(b.nome))
  }, [projetoId, apenasCarreteiro])
}

// ============================================================================
//  Encarregados / Equipes via cat_equipe_encarregado (UX inversa)
// ============================================================================
export function useEncarregadosDoProjeto(
  projetoId: number | null | undefined,
): Array<{ id: number; nome: string }> | undefined {
  return useLiveQuery(async () => {
    if (projetoId == null) return []
    const vinculos = await db.cat_equipe_encarregado.where('projetoId').equals(projetoId).toArray()
    // Distintos por encarregadoId
    const map = new Map<number, string>()
    for (const v of vinculos) {
      if (!map.has(v.encarregadoId)) map.set(v.encarregadoId, v.encarregadoNome)
    }
    return Array.from(map.entries())
      .map(([id, nome]) => ({ id, nome }))
      .sort((a, b) => a.nome.localeCompare(b.nome))
  }, [projetoId])
}

export function useEquipesDoEncarregado(
  projetoId: number | null | undefined,
  encarregadoId: number | null | undefined,
): Array<{ id: number; nome: string }> | undefined {
  return useLiveQuery(async () => {
    if (projetoId == null || encarregadoId == null) return []
    const vinculos = await db.cat_equipe_encarregado
      .where('[projetoId+equipeId+encarregadoId]')
      .between([projetoId, 0, encarregadoId], [projetoId, Number.MAX_SAFE_INTEGER, encarregadoId])
      .toArray()
    const filtered = vinculos.filter((v) => v.encarregadoId === encarregadoId)
    const map = new Map<number, string>()
    for (const v of filtered) {
      if (!map.has(v.equipeId)) map.set(v.equipeId, v.equipeNome)
    }
    return Array.from(map.entries())
      .map(([id, nome]) => ({ id, nome }))
      .sort((a, b) => a.nome.localeCompare(b.nome))
  }, [projetoId, encarregadoId])
}

// ============================================================================
//  Catálogos simples
// ============================================================================
export function useMotoristas() {
  return useLiveQuery(() => db.cat_motorista.orderBy('nome').toArray())
}
export function useApontadores() {
  return useLiveQuery(() => db.cat_apontador.orderBy('nome').toArray())
}
export function useUnidades() {
  return useLiveQuery(() => db.cat_unidade.orderBy('nome').toArray())
}
export function useMateriais() {
  return useLiveQuery(() => db.cat_material.orderBy('nome').toArray())
}
/**
 * @deprecated Use `useMateriaisRedePorProjeto(projetoId)`. O módulo Produção
 * passou a escopar por projeto via cdt_projeto_tem_material_rede.
 */
export function useMateriaisRede() {
  return useLiveQuery(() => db.cat_material_rede.orderBy('nome').toArray())
}
/** @deprecated Use `useDiametrosPorProjeto(projetoId)`. */
export function useDiametros() {
  return useLiveQuery(() => db.cat_diametro.orderBy('nome').toArray())
}
/** @deprecated Use `useTiposRedePorProjeto(projetoId)`. */
export function useTiposRede() {
  return useLiveQuery(() => db.cat_tipo_rede.orderBy('nome').toArray())
}
// Versões escopadas por projeto — usadas pelo módulo Produção. O sync popula
// cat_material_rede / cat_diametro / cat_tipo_rede com `projetoId` preenchido
// (per-project). Estes hooks filtram localmente.
export function useMateriaisRedePorProjeto(projetoId: number | null | undefined) {
  return useLiveQuery(
    (): Promise<CatMaterialRede[]> =>
      projetoId == null
        ? Promise.resolve([])
        : db.cat_material_rede.where('projetoId').equals(projetoId).sortBy('nome'),
    [projetoId],
  )
}
export function useDiametrosPorProjeto(projetoId: number | null | undefined) {
  return useLiveQuery(
    (): Promise<CatDiametro[]> =>
      projetoId == null
        ? Promise.resolve([])
        : db.cat_diametro.where('projetoId').equals(projetoId).sortBy('nome'),
    [projetoId],
  )
}
export function useTiposRedePorProjeto(projetoId: number | null | undefined) {
  return useLiveQuery(
    (): Promise<CatTipoRede[]> =>
      projetoId == null
        ? Promise.resolve([])
        : db.cat_tipo_rede.where('projetoId').equals(projetoId).sortBy('nome'),
    [projetoId],
  )
}
export function useMves(tps: string[]) {
  return useLiveQuery(
    () =>
      tps.length === 0
        ? db.cat_mve.toArray()
        : db.cat_mve.where('tp').anyOf(tps).sortBy('prefixo'),
    [tps.join(',')],
  )
}

// `useMvesPorObra` (filtro local por chefe admin) foi removido — não tinha
// como saber sobre vigência de contrato/projeto sem dados extras no Dexie.
// Carreteiro agora usa `useMvesCarreteiroPorObra` (online+fallback) em
// web/src/features/carreteiro/useMvesCarreteiro.ts.

export function useLocaisCargaPorProjeto(projetoId: number | null | undefined) {
  return useLiveQuery(
    async () => {
      if (projetoId == null) return []
      return db.cat_local_carga.where('projetoId').equals(projetoId).toArray()
    },
    [projetoId],
  )
}
// Locais de descarga são escopados por projeto no SIGA (cdt_local_descarga_
// pavimento_{urbana,rodoviario}.projeto_id). Picker do carreteiro filtra
// pelo projeto da obra atual. Quando projetoId é null, devolve [] (defesa —
// não queremos mostrar destinos de outros projetos).
export function useLocaisDescargaRod(projetoId: number | null | undefined) {
  return useLiveQuery(async () => {
    if (projetoId == null) return []
    const all = await db.cat_local_descarga_rod.toArray()
    return all
      .filter((l) => l.projetoId === projetoId)
      .sort((a, b) => (a.nome ?? '').localeCompare(b.nome ?? ''))
  }, [projetoId])
}
export function useLocaisDescargaUrb(projetoId: number | null | undefined) {
  return useLiveQuery(async () => {
    if (projetoId == null) return []
    const all = await db.cat_local_descarga_urb.toArray()
    return all
      .filter((l) => l.projetoId === projetoId)
      .sort((a, b) => (a.nome ?? '').localeCompare(b.nome ?? ''))
  }, [projetoId])
}
export function useTrechosPorProjeto(projetoId: number | null | undefined) {
  return useLiveQuery(
    () =>
      projetoId == null
        ? db.cat_trecho.orderBy('nome').toArray()
        : db.cat_trecho.where('projetoId').equals(projetoId).sortBy('nome'),
    [projetoId],
  )
}

// ============================================================================
//  Outbox — Produção
// ============================================================================

/** Últimas 50 linhas do outbox, mais recentes primeiro (debug + tela de pendências futura). */
export function useProducaoRecente(): OutProducao[] | undefined {
  return useLiveQuery(() =>
    db.out_producao.orderBy('capturedAt').reverse().limit(50).toArray(),
  )
}

/** Contagem por status — usado no badge da Home ("3 pendentes"). */
export function useProducaoCountByStatus(
  status: SyncStatus,
): number | undefined {
  return useLiveQuery(() => db.out_producao.where('syncStatus').equals(status).count(), [status])
}

/**
 * Agregado de sessão (chave = projeto+dt+encarregado+equipe). Reutiliza o índice
 * composto `[projetoId+dt]` para escanear só os registros do projeto, agrupa em
 * memória.
 *
 * Filtro (atualizado 2026-05-24): sessão é VISÍVEL apenas enquanto tem item
 * com status != SYNCED. Quando 100% sincronizou, some IMEDIATAMENTE da lista
 * (independente da data). UX: usuário não confunde "sincronizado" com "pendente"
 * — antes, sessões SYNCED do dia continuavam visíveis pra "continuar adicionando",
 * o que causava confusão sobre o que ainda estava pendente.
 *
 * Para continuar adicionando lançamentos no mesmo dia/equipe, basta criar nova
 * sessão — o backend faz merge no SIGA por chave (projeto+dt+enc+equipe), então
 * não duplica nem perde dado.
 */
export interface SessaoProducao {
  key: string
  projetoId: number
  projetoCodigo: string
  dt: string
  encarregadoId: number | null
  encarregadoNome: string | null
  equipeId: number | null
  equipeNome: string | null
  itens: OutProducao[]
  total: number
  pendentes: number
  ultimoCapturedAt: string
  statusDominante: SyncStatus
}

export function useSessoesDoProjeto(
  projetoId: number | null | undefined,
): SessaoProducao[] | undefined {
  return useLiveQuery(async (): Promise<SessaoProducao[]> => {
    if (projetoId == null) return []
    const rows = await db.out_producao.where('projetoId').equals(projetoId).toArray()
    const map = new Map<string, SessaoProducao>()
    for (const r of rows) {
      const k = `${r.projetoId}|${r.dt}|${r.encarregadoId ?? 0}|${r.equipeId ?? 0}`
      const ex = map.get(k)
      if (ex) {
        ex.itens.push(r)
      } else {
        map.set(k, {
          key: k,
          projetoId: r.projetoId,
          projetoCodigo: r.projetoCodigo,
          dt: r.dt,
          encarregadoId: r.encarregadoId,
          encarregadoNome: r.encarregadoNome,
          equipeId: r.equipeId,
          equipeNome: r.equipeNome,
          itens: [r],
          total: 0,
          pendentes: 0,
          ultimoCapturedAt: r.capturedAt,
          statusDominante: r.syncStatus,
        })
      }
    }
    const out: SessaoProducao[] = []
    for (const s of map.values()) {
      s.total = s.itens.length
      s.pendentes = s.itens.filter((i) => i.syncStatus !== 'SYNCED').length
      s.ultimoCapturedAt = s.itens.reduce(
        (acc, i) => (i.capturedAt > acc ? i.capturedAt : acc),
        s.itens[0].capturedAt,
      )
      // Status dominante: FAILED > SYNCING > PENDING > SYNCED
      if (s.itens.some((i) => i.syncStatus === 'FAILED')) s.statusDominante = 'FAILED'
      else if (s.itens.some((i) => i.syncStatus === 'SYNCING')) s.statusDominante = 'SYNCING'
      else if (s.itens.some((i) => i.syncStatus === 'PENDING')) s.statusDominante = 'PENDING'
      else s.statusDominante = 'SYNCED'

      // Visibilidade: SÓ sessões com pendência. Sincronizou some imediato.
      if (s.pendentes > 0) out.push(s)
    }
    // Ordena: mais recentes primeiro, depois encarregado
    return out.sort((a, b) => {
      if (a.dt !== b.dt) return b.dt.localeCompare(a.dt)
      return (a.encarregadoNome ?? '').localeCompare(b.encarregadoNome ?? '')
    })
  }, [projetoId])
}

/**
 * Contagens da outbox para badges. Produção + fotos não sincronizadas +
 * fichas de carreteiro na fila. Fichas REJEITADAS ficam fora (não vão ser
 * reenviadas) — ver `useCarreteiroRecusadas`.
 */
export function useOutboxCounts():
  | { producao: number; fotos: number; carreteiro: number; parteDiaria: number; total: number }
  | undefined {
  return useLiveQuery(async () => {
    const [prod, foto, carr, pd] = await Promise.all([
      db.out_producao.where('syncStatus').notEqual('SYNCED').count(),
      db.out_foto.where('syncStatus').notEqual('SYNCED').count(),
      db.out_carreteiro.where('syncStatus').anyOf('PENDING', 'SYNCING', 'FAILED').count(),
      db.out_parte_diaria.where('syncStatus').anyOf('PENDING', 'SYNCING', 'FAILED').count(),
    ])
    return { producao: prod, fotos: foto, carreteiro: carr, parteDiaria: pd, total: prod + foto + carr + pd }
  })
}

/** Partes diárias recusadas pelo servidor, mais recentes primeiro. */
export function useParteDiariaRecusadas(): OutParteDiaria[] | undefined {
  return useLiveQuery(async () => {
    const rows = await db.out_parte_diaria.where('syncStatus').equals('REJEITADA').toArray()
    return rows.sort((a, b) => b.capturedAt.localeCompare(a.capturedAt))
  })
}

/** Fichas de carreteiro recusadas pelo servidor, mais recentes primeiro. */
export function useCarreteiroRecusadas(): OutCarreteiro[] | undefined {
  return useLiveQuery(async () => {
    const rows = await db.out_carreteiro.where('syncStatus').equals('REJEITADA').toArray()
    return rows.sort((a, b) => b.capturedAt.localeCompare(a.capturedAt))
  })
}

/** Verifica se o catálogo principal foi populado pelo menos uma vez. */
export async function isCatalogPopulated(): Promise<boolean> {
  const n = await db.cat_projeto.count()
  return n > 0
}

// ============================================================================
//  Outbox — Fotos (filtrado por projeto, mais recentes primeiro)
// ============================================================================
/**
 * Janela de visibilidade da lista de fotos. Fotos mais antigas não somem do banco
 * (continuam acessíveis via SIGA + Supabase), mas ficam fora da UI de captura pra
 * a tela permanecer leve mesmo em obras com muitas fotos acumuladas.
 */
export const FOTOS_VISIBILITY_HOURS = 48

export interface FotosFeed {
  /** Fotos PENDENTES (não-SYNCED) — único feed visível na lista (decisão 2026-05-26). */
  pending: OutFoto[]
  /** Contagem total de SYNCED no projeto — exposto como contador secundário. */
  syncedCount: number
}

/**
 * Lista de fotos do projeto — MUDANÇA 2026-05-26:
 * mostra APENAS fotos pendentes de sync (PENDING/SYNCING/FAILED).
 * Fotos SYNCED desaparecem da lista do app (visíveis só no SIGA web/Galeria).
 * Foco do app é "o que falta enviar".
 */
export function useFotosDoProjeto(projetoId: number | null | undefined): FotosFeed | undefined {
  return useLiveQuery(async (): Promise<FotosFeed> => {
    if (projetoId == null) return { pending: [], syncedCount: 0 }
    const all = await db.out_foto.where('projetoId').equals(projetoId).toArray()
    const pending: OutFoto[] = []
    let syncedCount = 0
    for (const f of all) {
      if (f.syncStatus === 'SYNCED') syncedCount++
      else pending.push(f)
    }
    pending.sort((a, b) => b.capturedAt.localeCompare(a.capturedAt))
    return { pending, syncedCount }
  }, [projetoId])
}
